import { motion } from 'framer-motion';
import PageNavbar from '../../components/feature/PageNavbar';
import Footer from '../home/components/Footer';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import { useState } from 'react';
import {
  useSEO,
  generateWebPageSchema,
  generateServiceSchema,
  generateFAQSchema,
  generateBreadcrumbSchema,
} from '../../utils/seo';

export default function PricingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const features = [
    'Bis max. 7 Seiten',
    'Responsive Design',
    'SEO-Optimierung',
    'Kontaktformular',
    'SSL-Zertifikat',
    'DSGVO-Absicherung inkl. Datenschutzerklärung & Cookie-Management',
    '2 kostenlose Änderungen inklusive',
  ];

  const extras = [
    { label: 'Kleinere Änderung', price: '50€', note: 'pauschal' },
    { label: 'Redesign & größere Änderungen', price: '120€', note: 'pauschal' },
  ];

  const faqs = [
    {
      question: 'Was kostet eine professionelle Webseite?',
      answer:
        'Das Komplettpaket kostet 899€ einmalig und beinhaltet bis zu 7 Seiten, Responsive Design, SEO-Optimierung, Kontaktformular, SSL-Zertifikat, DSGVO-Absicherung inkl. Datenschutzerklärung & Cookie-Management sowie 2 kostenlose Änderungen.',
    },
    {
      question: 'Was ist alles im Preis enthalten?',
      answer:
        'Im Komplettpaket für 899€ sind enthalten: Bis zu 7 Seiten, Responsive Design für alle Geräte, SEO-Optimierung, ein Kontaktformular, SSL-Zertifikat für sichere Verbindungen, DSGVO-Absicherung mit Datenschutzerklärung & Cookie-Management sowie 2 kostenlose Änderungen.',
    },
    {
      question: 'Wie lange dauert die Erstellung einer Webseite?',
      answer:
        'Die Erstellung Ihrer professionellen Webseite dauert maximal 1 Woche. Sie erhalten regelmäßige Updates und können jederzeit Feedback geben.',
    },
    {
      question: 'Was zählt als kleinere Änderung?',
      answer:
        'Kleinere Änderungen umfassen z.B. Textanpassungen, Bildaustausch, Farbänderungen oder das Hinzufügen einzelner Elemente. Die ersten 2 Änderungen sind kostenlos inklusive. Weitere kleinere Änderungen kosten pauschal 50€.',
    },
    {
      question: 'Was zählt als Redesign oder größere Änderung?',
      answer:
        'Größere Änderungen umfassen z.B. strukturelle Umgestaltungen, neue Sektionen, Funktionserweiterungen oder ein komplettes Redesign einzelner Seiten. Diese kosten pauschal 120€.',
    },
    {
      question: 'Ist die Webseite DSGVO-konform?',
      answer:
        'Ja, absolut! Jede Webseite wird mit SSL-Zertifikat, Datenschutzerklärung und Cookie-Management ausgeliefert und erfüllt alle DSGVO-Anforderungen.',
    },
    {
      question: 'Kann ich einen AI-Chatbot hinzufügen?',
      answer:
        'Ja! AI-Chatbots können Kundenanfragen automatisch beantworten, Termine vereinbaren und die Conversion-Rate deutlich steigern. Sprechen Sie mich gerne darauf an.',
    },
    {
      question: 'Welche Zahlungsmethoden akzeptieren Sie?',
      answer: 'Ich akzeptiere Überweisung und PayPal. Bei größeren Projekten ist auch eine Ratenzahlung möglich.',
    },
    {
      question: 'Gibt es versteckte Kosten?',
      answer:
        'Nein! Der genannte Preis ist ein Festpreis. Zusätzliche Wünsche werden vorher transparent besprochen und kalkuliert.',
    },
    {
      question: 'Wie läuft die Zusammenarbeit ab?',
      answer:
        'Nach einem kostenlosen Erstgespräch erstelle ich ein individuelles Angebot. Nach Freigabe starte ich mit Design und Entwicklung. Sie erhalten regelmäßige Updates und können jederzeit Feedback geben.',
    },
  ];

  useSEO({
    title: 'Webdesign Preise Titisee-Neustadt - 899€ Komplettpaket',
    description:
      'Webdesign Titisee-Neustadt ab 899€: Professionelle Webseite mit 7 Seiten, SEO, DSGVO & AI-Chatbot. Faire Festpreise im Schwarzwald.',
    keywords:
      'Webdesign Preise Titisee-Neustadt, Webseite Kosten 79822, Webdesign Schwarzwald Preis',
    canonical: '/pricing',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          'Webdesign Preise - Transparente Kosten',
          'Faire und transparente Preise für professionelles Webdesign',
          '/pricing'
        ),
        generateServiceSchema(),
        generateFAQSchema(faqs),
        generateBreadcrumbSchema([
          { name: 'Startseite', url: '/' },
          { name: 'Preise', url: '/pricing' },
        ]),
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <PageNavbar />

      {/* Hero Section */}
      <section className="relative pt-32 sm:pt-40 pb-16 sm:pb-20 overflow-hidden bg-brand-beige-light">
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-caprasimo mb-4 sm:mb-6 text-brand-black px-4">
              Transparente Preise
            </h1>
            <p className="text-lg sm:text-xl text-brand-grey max-w-3xl mx-auto px-4">
              Ein Festpreis – keine versteckten Kosten. Alles, was Sie für Ihren
              professionellen Webauftritt brauchen.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Single Pricing Card */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="relative bg-white rounded-2xl p-6 sm:p-10 border-2 border-brand-green shadow-2xl"
          >
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <div className="bg-brand-green text-white px-6 py-2 rounded-full text-sm font-semibold whitespace-nowrap">
                Komplettpaket
              </div>
            </div>

            <div className="text-center mb-8 mt-2">
              <h2 className="text-2xl sm:text-3xl font-caprasimo text-brand-black mb-2">
                Webseite Komplett
              </h2>
              <p className="text-brand-grey text-sm">
                Alles, was Sie für einen professionellen Webauftritt brauchen
              </p>
            </div>

            <div className="text-center mb-8">
              <div className="flex items-baseline justify-center gap-2 flex-wrap">
                <span className="text-5xl sm:text-6xl font-bold text-brand-black">899€</span>
                <span className="text-brand-grey text-sm sm:text-base">einmalig</span>
              </div>
              {/* Frühbucherpreis Badge */}
              <div className="mt-3 flex items-center justify-center gap-2">
                <span className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-300 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                  <i className="ri-time-line text-sm"></i>
                  Einführungspreis – nur für kurze Zeit
                </span>
              </div>
              <p className="text-xs text-brand-grey mt-2 opacity-70">
                Regulärer Marktpreis: ab 2.000€ – Sie sparen über 55 %
              </p>
            </div>

            <a
              href="/contact"
              className="block w-full text-center py-4 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap mb-8 bg-brand-green hover:bg-brand-green-light text-white shadow-lg min-h-[44px] flex items-center justify-center"
            >
              Jetzt anfragen
            </a>

            <div className="space-y-4 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center bg-brand-green/10 rounded-full flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs text-brand-green"></i>
                  </div>
                  <span className="text-brand-grey text-sm">{feature}</span>
                </div>
              ))}
            </div>

            {/* Extras */}
            <div className="border-t-2 border-brand-beige pt-6">
              <h3 className="text-base sm:text-lg font-caprasimo text-brand-black mb-4">
                Weitere Änderungen
              </h3>
              <div className="space-y-3">
                {extras.map((extra, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-brand-beige-light rounded-lg px-4 py-3"
                  >
                    <span className="text-brand-grey text-sm">{extra.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-brand-black font-semibold text-sm">
                        {extra.price}
                      </span>
                      <span className="text-brand-grey text-xs">({extra.note})</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-16 sm:py-20 bg-brand-beige-light">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block bg-brand-green/10 text-brand-green text-xs font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wider">
              Preisvergleich
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-caprasimo text-brand-black mb-4">
              Was andere verlangen – was Sie zahlen
            </h2>
            <p className="text-brand-grey text-lg max-w-2xl mx-auto">
              Vergleichen Sie selbst: Große Agenturen berechnen ein Vielfaches für ähnliche Leistungen.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="overflow-x-auto rounded-2xl shadow-xl border border-brand-beige"
          >
            <table className="w-full min-w-[600px] bg-white">
              <thead>
                <tr className="border-b-2 border-brand-beige">
                  <th className="text-left px-6 py-5 text-brand-black font-caprasimo text-base w-2/5">
                    Leistung
                  </th>
                  <th className="px-6 py-5 text-center text-brand-grey font-semibold text-sm w-1/5">
                    <div className="flex flex-col items-center gap-1">
                      <i className="ri-building-2-line text-xl text-brand-grey/60"></i>
                      <span>Große Agentur</span>
                    </div>
                  </th>
                  <th className="px-6 py-5 text-center text-brand-grey font-semibold text-sm w-1/5">
                    <div className="flex flex-col items-center gap-1">
                      <i className="ri-store-2-line text-xl text-brand-grey/60"></i>
                      <span>Kleine Agentur</span>
                    </div>
                  </th>
                  <th className="px-6 py-5 text-center w-1/5 bg-brand-green/5">
                    <div className="flex flex-col items-center gap-1">
                      <i className="ri-award-line text-xl text-brand-green"></i>
                      <span className="text-brand-green font-bold text-sm">Mein Angebot</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    feature: 'Professionelle Webseite (bis 7 Seiten)',
                    big: '3.000 – 8.000€',
                    small: '1.500 – 3.000€',
                    mine: '899€',
                    highlight: true,
                  },
                  {
                    feature: 'Responsive Design (Mobil & Tablet)',
                    big: 'inklusive',
                    small: 'inklusive',
                    mine: 'inklusive',
                    highlight: false,
                  },
                  {
                    feature: 'SEO-Grundoptimierung',
                    big: '500 – 1.500€ extra',
                    small: '300 – 800€ extra',
                    mine: 'inklusive',
                    highlight: true,
                  },
                  {
                    feature: 'SSL-Zertifikat & Sicherheit',
                    big: 'inklusive',
                    small: 'inklusive',
                    mine: 'inklusive',
                    highlight: false,
                  },
                  {
                    feature: 'DSGVO-Absicherung & Datenschutz',
                    big: '200 – 600€ extra',
                    small: '150 – 400€ extra',
                    mine: 'inklusive',
                    highlight: true,
                  },
                  {
                    feature: 'Kontaktformular',
                    big: '150 – 400€ extra',
                    small: '100 – 250€ extra',
                    mine: 'inklusive',
                    highlight: false,
                  },
                  {
                    feature: 'Lieferzeit',
                    big: '4 – 12 Wochen',
                    small: '2 – 6 Wochen',
                    mine: 'max. 1 Woche',
                    highlight: true,
                  },
                  {
                    feature: 'Änderungen nach Fertigstellung',
                    big: 'Stundensatz 80–150€/h',
                    small: 'Stundensatz 50–90€/h',
                    mine: '2 kostenlos inklusive',
                    highlight: false,
                  },
                  {
                    feature: 'Persönlicher Ansprechpartner',
                    big: 'Projektmanager (wechselnd)',
                    small: 'Meistens',
                    mine: 'Immer direkt',
                    highlight: true,
                  },
                ].map((row, i) => (
                  <tr
                    key={i}
                    className={`border-b border-brand-beige/60 transition-colors hover:bg-brand-beige-light/40 ${
                      row.highlight ? '' : ''
                    }`}
                  >
                    <td className="px-6 py-4 text-brand-black font-medium text-sm">
                      {row.feature}
                    </td>
                    <td className="px-6 py-4 text-center text-brand-grey text-sm">
                      {row.big}
                    </td>
                    <td className="px-6 py-4 text-center text-brand-grey text-sm">
                      {row.small}
                    </td>
                    <td className="px-6 py-4 text-center bg-brand-green/5">
                      <span className="inline-flex items-center gap-1 text-brand-green font-semibold text-sm">
                        {row.mine === 'inklusive' ? (
                          <>
                            <i className="ri-check-line text-base"></i>
                            {row.mine}
                          </>
                        ) : (
                          row.mine
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
                {/* Total row */}
                <tr className="bg-brand-green/5 border-t-2 border-brand-green/30">
                  <td className="px-6 py-5 text-brand-black font-caprasimo text-base">
                    Gesamtkosten (geschätzt)
                  </td>
                  <td className="px-6 py-5 text-center">
                    <span className="text-red-500 font-bold text-base line-through opacity-70">
                      4.000 – 12.000€
                    </span>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <span className="text-red-400 font-bold text-base line-through opacity-70">
                      2.000 – 4.500€
                    </span>
                  </td>
                  <td className="px-6 py-5 text-center bg-brand-green/10">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-brand-green font-bold text-2xl">899€</span>
                      <span className="text-brand-green text-xs font-semibold bg-brand-green/10 px-2 py-0.5 rounded-full">
                        Einführungspreis
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-center text-xs text-brand-grey mt-4 opacity-60"
          >
            * Preise basieren auf Marktdurchschnittswerten deutscher Webdesign-Agenturen (2024/2025). Individuelle Angebote können abweichen.
          </motion.p>

          {/* CTA unter Vergleichstabelle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-10 flex flex-col items-center gap-4"
          >
            <p className="text-brand-grey text-base text-center max-w-xl">
              Überzeugt? Sichern Sie sich jetzt Ihren <strong className="text-brand-black">Einführungspreis von 899€</strong> – bevor der Preis steigt.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-3 bg-brand-green hover:bg-brand-green-light text-white px-10 py-4 rounded-lg text-lg font-semibold transition-all cursor-pointer whitespace-nowrap shadow-xl hover:shadow-2xl hover:scale-105"
            >
              <i className="ri-send-plane-line text-xl"></i>
              Jetzt anfragen
            </a>
            <span className="text-xs text-brand-grey opacity-60 flex items-center gap-1">
              <i className="ri-shield-check-line"></i>
              Kostenlos &amp; unverbindlich – Antwort innerhalb von 24h
            </span>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-brand-beige-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-caprasimo mb-4 sm:mb-6 text-brand-black px-4">
              Häufige Fragen zu den Preisen
            </h2>
            <p className="text-lg sm:text-xl text-brand-grey px-4">
              Alles, was Sie über meine Preise wissen müssen
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className={`bg-white rounded-xl overflow-hidden transition-all border-2 ${
                  openFaq === index ? 'border-brand-green shadow-lg' : 'border-brand-beige'
                }`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-4 sm:px-8 py-5 sm:py-6 flex items-center justify-between text-left hover:bg-brand-beige-light/50 transition-colors cursor-pointer min-h-[44px]"
                >
                  <span
                    className={`text-base sm:text-lg font-semibold pr-4 sm:pr-8 transition-colors ${
                      openFaq === index ? 'text-brand-green' : 'text-brand-black'
                    }`}
                  >
                    {faq.question}
                  </span>
                  <div
                    className={`w-11 h-11 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${
                      openFaq === index ? 'bg-brand-green rotate-180' : 'bg-brand-beige-light'
                    }`}
                  >
                    <i
                      className={`ri-arrow-down-s-line text-xl ${
                        openFaq === index ? 'text-white' : 'text-brand-green'
                      }`}
                    ></i>
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-4 sm:px-8 pb-5 sm:pb-6 text-brand-grey leading-relaxed text-sm sm:text-base">
                    {faq.answer}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-caprasimo text-brand-black mb-4 sm:mb-6 px-4">
            Noch Fragen?
          </h2>
          <p className="text-lg sm:text-xl text-brand-grey mb-8 sm:mb-10 max-w-2xl mx-auto px-4">
            Ich berate Sie gerne persönlich und erstelle ein individuelles Angebot für Ihr Projekt.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 px-4">
            <a
              href="/contact"
              className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-light text-white px-8 sm:px-10 py-4 rounded-lg text-base sm:text-lg font-semibold transition-all cursor-pointer whitespace-nowrap shadow-lg min-h-[44px] flex items-center justify-center"
            >
              Kostenloses Erstgespräch
            </a>
            <a
              href="tel:01623651571"
              className="w-full sm:w-auto bg-brand-beige-light hover:bg-brand-beige text-brand-black px-8 sm:px-10 py-4 rounded-lg text-base sm:text-lg font-semibold transition-all cursor-pointer whitespace-nowrap border border-brand-beige flex items-center justify-center gap-3 min-h-[44px]"
            >
              <i className="ri-phone-line"></i>
              0162 365 1571
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTopButton />
    </div>
  );
}